import { z } from "zod";

import { getColor } from "../getColor";

/**
 * Gets the contrast ratio between two colors to meet WCAG 2.0 AA standards
 * Based on:
 * https://blog.cristiana.tech/calculating-color-contrast-in-typescript-using-web-content-accessibility-guidelines-wcag
 */

// zod schema for a hexadecimal color, either 3, 4, 6, or 8 characters long (color hex + optional opacity), starting with #
const hexRegex = /^#(?:[0-9a-fA-F]{3,4}){1,2}$/;
export const hexadecimalColorSchema = z.string().regex(hexRegex);

export type RGB = [number, number, number];
export type Hex = z.infer<typeof hexadecimalColorSchema>;

/**
 * Function to get the contrast ratio between foreground and background colors
 * implementing the procedure described in WCAG 2.0
 * https://www.w3.org/TR/WCAG20-TECHS/G18.html#G18-tests
 */
export function getContrast(foreground: Hex | RGB, background: Hex | RGB) {
	let foregroundRGB: RGB;
	let backgroundRGB: RGB;

	if (typeof foreground === "string") {
		foregroundRGB = hexToRGB(foreground);
	} else if (foreground) {
		foregroundRGB = foreground;
	} else {
		foregroundRGB = [0, 0, 0];
	}

	if (typeof background === "string") {
		backgroundRGB = hexToRGB(background);
	} else if (background) {
		backgroundRGB = background;
	} else {
		backgroundRGB = [0, 0, 0];
	}

	const l1 = relativeLuminance(foregroundRGB) + 0.05;
	const l2 = relativeLuminance(backgroundRGB) + 0.05;
	return relativeLuminance(backgroundRGB) > relativeLuminance(foregroundRGB) ? l2 / l1 : l1 / l2;
}

/**
 * Function to calculate relative luminance from RGB values
 */
function relativeLuminance(rgb: RGB) {
	const [r, g, b] = rgb.map((value) => {
		const normalized = value / 255;
		return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
	});
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Function to convert to RGB values from hex
 * @param hex - hex color value like "#ae34fg"
 * @returns RGB values like [174, 52, 255]
 */
export function hexToRGB(hex: string) {
	hex = hex.slice(1);
	if (hex.length === 3) {
		hex = hex
			.split("")
			.map((value) => `${value}${value}`)
			.join("");
	}
	const value = parseInt(hex, 16);
	const r = (value >> 16) & 255;
	const g = (value >> 8) & 255;
	const b = value & 255;

	return [r, g, b] as RGB;
}

/**
 * Takes a list of colors and returns the first color meeting a contrast ratio standard
 * https://www.w3.org/TR/WCAG20-TECHS/G18.html (4.5:1)
 * @param options - options for the function
 * @param background
 * @returns the first color that meets the contrast ratio standard,
 * or the option with the highest contrast ratio if none meet the standard
 *
 * default options are neutral 0 #fff and primary 9 #031A38.
 */
interface IChooseBestContrastingColorArgs {
	background: Hex | RGB;
	standard?: number;
	options?: (Hex | RGB)[];
}
export const chooseBestContrastingColor = (args: IChooseBestContrastingColorArgs): Hex | RGB => {
	// parse args and set defaults
	const {
		background,
		options = [getColor("neutral", 0) as Hex, getColor("primary", 9) as Hex],
		standard = 4.5,
	} = args;

	// initialize
	let bestContrast = 0;
	let bestOption = options[0];

	// loop through options and find the best contrast
	for (const option of options) {
		const contrast = getContrast(option, background);
		if (contrast > bestContrast) {
			bestContrast = contrast;
			bestOption = option;
			// return once if we have met the  standard
			if (contrast >= standard) {
				return bestOption;
			}
		}
	}
	// return the best option if none have met the standard
	return bestOption;
};
